<template>
  <div>
    <div class="px-2 pt-2">
      <b-row class="align-items-start">
        <b-col>
          <div class="text-md text-thick mb-2">
            Video - {{ name }}
          </div>
        </b-col>
      </b-row>
      <b-row>
        <b-col
          cols="12"
          sm="6"
        >
          <input-field
            name="preset_video-codec"
            label="Video codec"
            label-position="top"
            :info="videoInfo.videoCodec"
            info-position="label"
            @openInfo="openInfo"
          >
            <div>
              {{
                getText(
                  videoDescription.codecTypeOptions,
                  videoDescription.codecType,
                )
              }}
            </div>
          </input-field>
        </b-col>
        <b-col
          cols="12"
          sm="6"
        >
          <input-field
            name="preset_video-resolution"
            label="Resolution (w x h)"
            label-position="top"
            :info="videoInfo.resolution"
            info-position="label"
            @openInfo="openInfo"
          >
            <div>
              {{ videoDescription.width }} x {{ videoDescription.height }}
            </div>
          </input-field>
        </b-col>
      </b-row>
      <b-row>
        <b-col
          cols="12"
          sm="6"
        >
          <input-field
            name="preset_video-rate_control_mode"
            label="Rate control mode"
            label-position="top"
            :info="videoInfo.rateControlMode"
            info-position="label"
            @openInfo="openInfo"
          >
            <div>
              {{
                getText(
                  videoDescription.rateControlModeOptions,
                  videoDescription.rateControlMode,
                )
              }}
            </div>
          </input-field>
        </b-col>
        <b-col
          cols="12"
          sm="6"
        >
          <input-field
            name="preset_video-quality_tuning_level"
            label="Quality tuning level"
            label-position="top"
            :info="videoInfo.qualityTuningLevel"
            info-position="label"
            @openInfo="openInfo"
          >
            <div>
              {{
                getText(
                  videoDescription.qualityTuningLevelOptions,
                  videoDescription.qualityTuningLevel,
                )
              }}
            </div>
          </input-field>
        </b-col>
        <!-- here -->
        <b-col
          cols="12"
          sm="6"
        >
          <input-field
            name="preset_video-fragmentationType"
            label="Fragmentation Type"
            label-position="top"
            :info="videoInfo.fragmentationType"
            info-position="label"
            @openInfo="openInfo"
          >
            <div>
              {{
                getText(
                  videoDescription.fragmentationTypeOptions,
                  videoDescription.fragmentationType,
                )
              }}
            </div>
          </input-field>
        </b-col>
        <b-col
          cols="12"
          sm="6"
        >
          <input-field
            name="preset_video-presetOption"
            label="Preset Options"
            label-position="top"
            :info="videoInfo.presetOption"
            info-position="label"
            @openInfo="openInfo"
          >
            <div>
              {{
                getText(
                  videoDescription.presetOptions,
                  videoDescription.presetOption,
                )
              }}
            </div>
          </input-field>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <input-field
            name="preset_video-bitrate"
            label="Bitrate (Kbit/s)"
            label-position="top"
            :info="videoInfo.bitrate"
            info-position="label"
            @openInfo="openInfo"
          >
            <div>
              {{ videoDescription.bitrate / 1000 }}
            </div>
          </input-field>
        </b-col>
        <b-col />
      </b-row>
    </div>

    <collapsable title="Framerate">
      <b-row>
        <b-col
          cols="12"
          sm="6"
        >
          <input-field
            name="preset_video-fr_control"
            label="Framerate"
            label-position="top"
            :info="videoInfo.framerate"
            info-position="label"
            @openInfo="openInfo"
          >
            {{ framerateControl.text }}
          </input-field>
        </b-col>
        <b-col
          v-if="!isFramerateFollowSource"
          cols="12"
          sm="6"
        >
          <input-field
            name="preset_video-framerate_numerator"
            label-position="top"
            label="Numerator/Denominator"
          >
            {{ videoDescription.framerateNumerator }} /
            {{ videoDescription.framerateDenominator }}
          </input-field>
        </b-col>
      </b-row>
    </collapsable>
    <collapsable title="Aspect ratio">
      <b-row>
        <b-col
          cols="12"
          sm="6"
        >
          <input-field
            name="preset_video-aspect_ratio_control"
            label="Pixel aspect ratio"
            label-position="top"
            :info="videoInfo.aspectRatio"
            info-position="label"
            @openInfo="openInfo"
          >
            {{ aspectRatioControl.text }}
          </input-field>
        </b-col>
        <b-col
          v-if="!isAspectRatioFollowSource"
          cols="12"
          sm="6"
        >
          <input-field
            name="preset_video-aspect_ratio_parnumerator"
            label-position="top"
            label="Numerator / Denominator"
          >
            {{ videoDescription.parNumerator }} /
            {{ videoDescription.parDenominator }}
          </input-field>
        </b-col>
      </b-row>
    </collapsable>
    <collapsable
      title="GOP structure"
      :has-errors="collapsableItems.gop.hasErrors"
    >
      <b-row>
        <b-col
          cols="12"
          sm="6"
        >
          <input-field
            name="preset_video-gop_size"
            label="GOP size"
            label-position="top"
            :info="videoInfo.gopSize"
            info-position="label"
            @openInfo="openInfo"
          >
            <div>
              {{ videoDescription.gopSize }}
            </div>
          </input-field>
        </b-col>
        <b-col
          cols="12"
          sm="6"
        >
          <input-field
            name="preset_video-gop_unit"
            label="Unit"
            label-position="top"
            :info="videoInfo.gopUnit"
            info-position="label"
            @openInfo="openInfo"
          >
            <div>
              {{
                getText(
                  videoDescription.gopSizeUnitOptions,
                  videoDescription.gopSizeUnit,
                )
              }}
            </div>
          </input-field>
        </b-col>
      </b-row>
      <b-row>
        <b-col
          cols="12"
          sm="6"
        >
          <input-field
            name="preset_video-gop_bframes"
            label="B frames between reference frames"
            label-position="top"
            :info="videoInfo.bFrames"
            info-position="label"
            @openInfo="openInfo"
          >
            <div>
              {{ videoDescription.numberBFramesBetweenReferenceFrames }}
            </div>
          </input-field>
        </b-col>
        <b-col />
      </b-row>
    </collapsable>
    <collapsable title="Codec Details">
      <b-row>
        <b-col cols="12">
          <input-field
            name="preset_video-codec_details_profile"
            label="Profile"
            label-position="top"
            :info="videoInfo.codecProfile"
            info-position="label"
            @openInfo="openInfo"
          >
            <div>
              {{
                getText(
                  videoDescription.codecProfileOptions,
                  videoDescription.codecProfile,
                )
              }}
            </div>
          </input-field>
        </b-col>
        <b-col sm="6">
          <input-field
            name="preset_video-codec_details_level"
            label="Level"
            label-position="top"
            :info="videoInfo.codecLevel"
            info-position="label"
            @openInfo="openInfo"
          >
            <div>
              {{ videoDescription.codecLevel }}
            </div>
          </input-field>
        </b-col>
      </b-row>
      <b-row>
        <b-col
          cols="12"
          sm="6"
        >
          <input-field
            name="preset_video-color_metadata"
            label="Color metadata"
            label-position="top"
            :info="videoInfo.colorMetadata"
            info-position="label"
            @openInfo="openInfo"
          >
            <div>
              {{
                getText(
                  videoDescription.colorMetadataOptions,
                  videoDescription.colorMetadata,
                )
              }}
            </div>
          </input-field>
        </b-col>
        <b-col />
      </b-row>
    </collapsable>
    <collapsable
      is-visible
      title="Preprocessors"
    >
      <b-row>
        <b-col
          cols="12"
          sm="6"
        >
          <input-field
            name="preset_video-preprocessors_deinterlace"
            label="Deinterlacer"
            label-position="right"
            :info="videoInfo.deinterlacer"
            info-position="label"
            @openInfo="openInfo"
          >
            <b-form-checkbox
              id="preset_video-preprocessors_deinterlace"
              v-model="videoDescription.deinterlace"
              name="check-button"
              disabled
              switch
              stacked
            />
          </input-field>
        </b-col>
        <b-col />
      </b-row>
    </collapsable>
  </div>
</template>

<script>
import Collapsable from '@/components/Collapsable.vue'

import InputField from '@/components/form/InputField.vue'

import helpers from '@/mixins/helpers'

import videoInfo from '../settings/videoInfo'

const CUSTOM_OPTION = { text: 'Custom', numerator: '', denominator: '' }
// eslint-disable-next-line no-unused-vars
const SOURCE_OPTION = {
  text: 'Follow Source',
  numerator: '0',
  denominator: '0',
}

export default {
  name: 'VideoSettings',
  components: {
    InputField,
    Collapsable,
  },
  mixins: [helpers],
  props: {
    edit: {
      type: Boolean,
      default: false,
    },
    isEditable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      videoInfo,
      collapsableItems: {
        gop: { hasErrors: '' },
      },
      videoDescription: null,
      TEXT: 0,
      NUMERATOR: 1,
      DENOMINATOR: 2,
      aspectRatioControl: {
        text: 'Follow Source',
        numerator: '0',
        denominator: '0',
      },
      framerateControl: {
        text: 'Follow Source',
        numerator: '0',
        denominator: '0',
      },
    }
  },
  computed: {
    isFramerateFollowSource() {
      return this.framerateControl.text === SOURCE_OPTION.text
    },
    isAspectRatioFollowSource() {
      return this.aspectRatioControl.text === SOURCE_OPTION.text
    },
    name() {
      return this.videoDescription.codecTypeOptions.find(
        cto => cto.id === this.videoDescription.codecType,
      ).value
    },
    getVideoFrameRateOptions() {
      return this.videoDescription.videoFrameRateOptions.map(fr => ({
        text: fr[this.TEXT],
        value: {
          text: fr[this.TEXT],
          numerator: fr[this.NUMERATOR],
          denominator: fr[this.DENOMINATOR],
        },
      }))
    },
    getVideoPixelRatioOptions() {
      return this.videoDescription.videoPixelRatioOptions.map(fr => ({
        text: fr[this.TEXT],
        value: {
          text: fr[this.TEXT],
          numerator: fr[this.NUMERATOR],
          denominator: fr[this.DENOMINATOR],
        },
      }))
    },
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      const emptyModel = {
        ...this.$store.state.outputPresets.emptyOutputPreset.videoDescription,
      }
      this.videoDescription = {
        ...(this.$store.state.outputPresets?.outputPreset?.videoDescription
          || emptyModel),
      }
      this.initFrameRateControl()
      this.initAspectRatioControl()
    },
    initFrameRateControl() {
      const n = this.videoDescription.framerateNumerator
      const d = this.videoDescription.framerateDenominator
      const findControl = this.getVideoFrameRateOptions.find(
        op => op.value.numerator === `${n}` && op.value.denominator === `${d}`,
      )
      if (findControl) {
        this.framerateControl = findControl.value
      } else if (n === '0' || d === '0') {
        this.framerateControl = SOURCE_OPTION
      } else {
        this.framerateControl = CUSTOM_OPTION
      }
    },
    initAspectRatioControl() {
      const n = this.videoDescription.parNumerator
      const d = this.videoDescription.parDenominator
      const findControl = this.getVideoPixelRatioOptions.find(op => {
        const temp = op.value.numerator === `${n}` && op.value.denominator === `${d}`
        return temp
      })
      if (findControl) {
        this.aspectRatioControl = findControl.value
      } else if (n === '0' || d === '0') {
        this.aspectRatioControl = SOURCE_OPTION
      } else {
        this.aspectRatioControl = CUSTOM_OPTION
      }
    },
    isCustom(opt) {
      return opt.text === CUSTOM_OPTION.text
    },
    openInfo(e) {
      this.$emit('openInfo', e)
    },
  },
}
</script>
